import type { Stripe } from '@stripe/stripe-js';
import { beginCheckout, endCheckout } from '@/shared/utils/checkoutInProgress';

const PAYMENT_SYSTEM = (import.meta.env.REACT_APP_PAYMENT_SYSTEM || '').toLowerCase();

export const isPaddle = PAYMENT_SYSTEM === 'paddle';
export const isStripe = PAYMENT_SYSTEM === 'stripe';
export const isPaymentEnabled = isPaddle || isStripe;

let loadStripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> | null => {
  if (!isStripe) return null;
  if (!loadStripePromise) {
    loadStripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
    );
  }
  return loadStripePromise;
};

interface ScheduledChange {
  action?: string;
  effectiveAt?: string;
}

export interface SubscriptionInfoType {
  id?: string;
  status?: string;
  planName?: string;
  scheduledChange?: ScheduledChange;
  trialEndsAt?: string;
  nextBilledAt?: string;
  hasUnpaidInvoice?: boolean;
  isExtendedTrial?: boolean;
  hostedInvoiceUrl?: string | null;
}

export interface PlanType {
  id: string;
  name: string;
  amount: string;
  billingCycle: string;
  sourceContent: number;
  content: number;
  aiImages: number;
}

export interface OriginalStateType {
  itemId: string;
  priceId: string;
  quantity: number;
}

export const getResponseMessage = (error: unknown): string | undefined =>
  (error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;

export const FREE_PLAN_NAMES = ['Evaluation', 'Free'];
export const getPlanName = (info?: SubscriptionInfoType): string =>
  info?.planName || 'Evaluation';

export const isFreePlanName = (name?: string | null): boolean =>
  !name || FREE_PLAN_NAMES.includes(name);

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const POPUP_POLL_MS = 500;

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

export const openCenteredPopup = (
  name: string,
  url = 'about:blank'
): Window | null => {
  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
  return window.open(
    url,
    name,
    `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`
  );
};

export const closePopup = (popup: Window | null): void => {
  if (popup && !popup.closed) {
    try {
      popup.close();
    } catch {
      /* ignore — cross-origin or already gone */
    }
  }
};

export const waitForPopupClose = (
  popup: Window,
  intervalMs = POPUP_POLL_MS
): Promise<void> =>
  new Promise((resolve) => {
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        resolve();
      }
    }, intervalMs);
  });

export const startCheckoutOverlay = (message: string): (() => void) => {
  beginCheckout(message);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    endCheckout();
  };
};

export const SUBSCRIPTION_MIN_WIDTH = '820px';